// Journal/Logs Page - Rich text journal entries with category filtering
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Plus, Filter, Search, Calendar, Tag, ArrowLeft, Edit, Trash2, Copy } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useEntity } from '@/hooks/useEntity';
import { journalEntryEntityConfig, JournalEntry } from '@/entities/Journal';
import { supabase } from '@/lib/supabase';

// Custom CSS for Quill editor
const editorStyle = `
  .journal-editor .ql-container {
    min-height: 300px;
    font-size: 16px;
    background: white;
  }
  .journal-editor .ql-editor {
    min-height: 300px;
    color: #1e293b;
  }
  .journal-editor .ql-editor.ql-blank::before {
    color: #94a3b8;
    font-style: normal;
  }
`;

const CATEGORIES = ['All', 'Course', 'Research', 'Personal', 'Work', 'Other'] as const;

export default function JournalPage() {
  const navigate = useNavigate();
  const isEmbedded = window.location !== window.parent.location; // Check if in iframe
  const { items: entries, loading, create, update, delete: deleteEntry, reload } = useEntity<JournalEntry>(journalEntryEntityConfig);
  
  const [userId, setUserId] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedClass, setSelectedClass] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingEntry, setEditingEntry] = useState<JournalEntry | null>(null);
  const [classes, setClasses] = useState<Array<{id: string, title: string}>>([]);
  
  // New entry form state
  const [newSubject, setNewSubject] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newClassId, setNewClassId] = useState<string>('');
  const [newCategory, setNewCategory] = useState<'Course' | 'Research' | 'Personal' | 'Work' | 'Other'>('Personal');
  const [newContent, setNewContent] = useState('');

  // Get current user and load classes
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || '');
      
      // Load classes from localStorage
      if (user?.id) {
        const notebooksKey = `whipt_organizer_notebooks_${user.id}`;
        const stored = localStorage.getItem(notebooksKey);
        if (stored) {
          try {
            const notebooks = JSON.parse(stored);
            const classList = notebooks.map((nb: any) => ({
              id: nb.id,
              title: nb.title || 'Untitled Class'
            }));
            setClasses(classList);
          } catch (err) {
            console.error('Failed to load classes:', err);
          }
        }
      }
    };
    getUser();
    reload();
  }, [reload]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    let filtered = entries.filter(e => e.userId === userId);
    
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(e => e.category === selectedCategory);
    }
    
    if (selectedClass !== 'All') {
      filtered = filtered.filter(e => e.classId === selectedClass);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(e => 
        e.title.toLowerCase().includes(query) ||
        e.content.toLowerCase().includes(query) ||
        (e.subject && e.subject.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [entries, userId, selectedCategory, selectedClass, searchQuery]);

  const handleCreateEntry = async () => {
    if (!newTitle.trim() || !newContent.trim()) {
      alert('Please enter both title and content');
      return;
    }

    const now = new Date();
    const selectedClassData = classes.find(c => c.id === newClassId);
    
    if (editingEntry) {
      // Update existing entry
      const updated: Partial<JournalEntry> = {
        category: newCategory,
        subject: newSubject.trim() || undefined,
        title: newTitle.trim(),
        classId: newClassId || undefined,
        className: selectedClassData?.title,
        content: newContent,
        updatedAt: now.toISOString(),
      };
      await update(editingEntry.id, updated);
      setEditingEntry(null);
    } else {
      // Create new entry
      const entry: Partial<JournalEntry> = {
        userId,
        category: newCategory,
        subject: newSubject.trim() || undefined,
        title: newTitle.trim(),
        classId: newClassId || undefined,
        className: selectedClassData?.title,
        entryDate: now.toISOString().split('T')[0],
        entryTime: now.toTimeString().split(' ')[0],
        content: newContent,
        entryType: 'journal',
        createdAt: now.toISOString(),
        updatedAt: now.toISOString(),
      };
      await create(entry as JournalEntry);
    }
    
    // Reset form
    setNewSubject('');
    setNewTitle('');
    setNewClassId('');
    setNewContent('');
    setIsCreating(false);
    await reload();
  };

  const handleEdit = (entry: JournalEntry) => {
    setEditingEntry(entry);
    setNewSubject(entry.subject || '');
    setNewTitle(entry.title);
    setNewClassId(entry.classId || '');
    setNewCategory(entry.category);
    setNewContent(entry.content);
    setIsCreating(true);
  };
  
  const handleDuplicate = async (entry: JournalEntry) => {
    const now = new Date();
    const duplicated: Partial<JournalEntry> = {
      userId: entry.userId,
      category: entry.category,
      subject: entry.subject ? `${entry.subject} (Copy)` : undefined,
      title: `${entry.title} (Copy)`,
      classId: entry.classId,
      className: entry.className,
      entryDate: now.toISOString().split('T')[0],
      entryTime: now.toTimeString().split(' ')[0],
      content: entry.content,
      entryType: entry.entryType,
      createdAt: now.toISOString(),
      updatedAt: now.toISOString(),
    };
    await create(duplicated as JournalEntry);
    await reload();
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      console.log('🗑️ Deleting journal entry:', id);
      await deleteEntry(id);
      await reload();
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setIsCreating(false);
    setNewSubject('');
    setNewTitle('');
    setNewClassId('');
    setNewContent('');
    setNewCategory('Personal');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      Course: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      Research: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      Personal: 'bg-green-500/20 text-green-300 border-green-500/30',
      Work: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      Other: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    };
    return colors[category as keyof typeof colors] || colors.Other;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100">
      {/* Inject custom CSS for Quill editor */}
      <style>{editorStyle}</style>
      
      {/* Header */}
      {!isEmbedded && (
        <div className="border-b border-white/10 bg-slate-900/50 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3">
                  <BookOpen className="w-7 h-7 text-amber-400" />
                  <h1 className="text-2xl font-bold">Journal & Logs</h1>
                </div>
              </div>
              
              <button
                onClick={() => setIsCreating(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 rounded-lg font-semibold transition-all shadow-lg"
              >
                <Plus className="w-5 h-5" />
                New Entry
              </button>
            </div>

            {/* Filters */}
            <div className="mt-4 flex flex-wrap items-center gap-4">
              {/* Category Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-400 font-medium">Category:</span>
                <div className="flex gap-2">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        selectedCategory === cat
                          ? 'bg-amber-500 text-white'
                          : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Class Filter */}
              {classes.length > 0 && (
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-slate-400" />
                  <span className="text-xs text-slate-400 font-medium">Class:</span>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="px-3 py-1 rounded-lg text-sm bg-slate-700/50 border border-slate-600 text-slate-300 focus:outline-none focus:border-amber-500"
                  >
                    <option value="All">All Classes</option>
                    {classes.map(cls => (
                      <option key={cls.id} value={cls.id}>{cls.title}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Search */}
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search entries..."
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-slate-200 placeholder-slate-400 focus:outline-none focus:border-amber-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className={`max-w-7xl mx-auto px-6 ${isEmbedded ? 'py-4' : 'py-8'}`}>
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading entries...</div>
        ) : isCreating ? (
          // Create Entry Form
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-800/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 shadow-2xl">
              <h2 className="text-xl font-bold mb-4">{editingEntry ? 'Edit Journal Entry' : 'New Journal Entry'}</h2>
              
              <div className="space-y-4">
                {/* Subject (Log Number) */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Subject / Log Number <span className="text-slate-500 text-xs">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={newSubject}
                    onChange={(e) => setNewSubject(e.target.value)}
                    placeholder="e.g., Log #4, Week 2 Reflection, Module 3..."
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-slate-200 placeholder-slate-400 focus:outline-none focus:border-amber-500"
                  />
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    placeholder="Entry title..."
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-slate-200 placeholder-slate-400 focus:outline-none focus:border-amber-500"
                  />
                </div>

                {/* Class Selection */}
                {classes.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Class <span className="text-slate-500 text-xs">(optional)</span>
                    </label>
                    <select
                      value={newClassId}
                      onChange={(e) => setNewClassId(e.target.value)}
                      className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-slate-200 focus:outline-none focus:border-amber-500"
                    >
                      <option value="">No Class</option>
                      {classes.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.title}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Category</label>
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value as any)}
                    className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-2 text-slate-200 focus:outline-none focus:border-amber-500"
                  >
                    {CATEGORIES.filter(c => c !== 'All').map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Content - Rich Text Editor */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Content</label>
                  <div className="bg-white rounded-lg min-h-[350px]">
                    <ReactQuill
                      theme="snow"
                      value={newContent}
                      onChange={setNewContent}
                      placeholder="Write your entry..."
                      className="journal-editor"
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          ['blockquote', 'code-block'],
                          ['link'],
                          ['clean']
                        ]
                      }}
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateEntry}
                    className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 rounded-lg font-semibold transition-all"
                  >
                    {editingEntry ? 'Update Entry' : 'Save Entry'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No journal entries yet</p>
            <button
              onClick={() => setIsCreating(true)}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg font-semibold transition-colors"
            >
              Create Your First Entry
            </button>
          </div>
        ) : (
          // Entries List
          <div className="grid gap-4">
            {filteredEntries.map(entry => (
              <div
                key={entry.id}
                className="bg-slate-800/50 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:border-amber-500/30 transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    {entry.subject && (
                      <div className="text-xs font-mono text-amber-400 mb-1">📋 {entry.subject}</div>
                    )}
                    <h3 className="text-lg font-semibold text-slate-100 mb-2">{entry.title}</h3>
                    <div className="flex items-center gap-3 text-sm text-slate-400 flex-wrap">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(entry.entryDate)}
                      </div>
                      {entry.className && (
                        <div className="flex items-center gap-1 text-blue-300">
                          <BookOpen className="w-3 h-3" />
                          {entry.className}
                        </div>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getCategoryColor(entry.category)}`}>
                        <Tag className="w-3 h-3 inline mr-1" />
                        {entry.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(entry);
                      }}
                      className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-blue-400 transition-colors"
                      title="Duplicate entry"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(entry);
                      }}
                      className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-amber-400 transition-colors"
                      title="Edit entry"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(entry.id);
                      }}
                      className="p-2 rounded-lg hover:bg-slate-700/50 text-slate-400 hover:text-red-400 transition-colors"
                      title="Delete entry"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div 
                  className="prose prose-invert prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: entry.content }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
